import { Bold, Italic, Minus, Plus, Trash2 } from 'lucide-react'
import { type CSSProperties, useEffect, useLayoutEffect, useRef } from 'react'
import { BASELINE_EM, cssFont, TEXT_PAD } from '@/lib/fonts'
import { NOTE_SIZE } from '@/lib/geometry'
import { useDoc } from '@/store/documentStore'
import { SWATCHES, useUi } from '@/store/uiStore'
import type { FontFamily, FreeTextAnn, NoteAnn, PageRef, TextEditAnn, TextStyle } from '@/types'

const stop = (e: { stopPropagation(): void }) => e.stopPropagation()

/**
 * Run `fn` when the component really unmounts. React StrictMode mounts → unmounts → remounts
 * every component in development; deferring and checking a generation counter skips that
 * fake unmount.
 */
function useUnmount(fn: () => void) {
  const gen = useRef(0)
  const latest = useRef(fn)
  latest.current = fn
  useEffect(() => {
    const mine = ++gen.current
    return () => {
      setTimeout(() => {
        if (gen.current === mine) latest.current()
      }, 0)
    }
  }, [])
}

/** Editing session helper: one undo step per editing session. */
function useEditSession(id: string, freshAtMount = false) {
  const before = useRef(useDoc.getState().snapshot())
  const fresh = useRef(freshAtMount).current
  const done = useRef(false)
  return {
    update(patch: Parameters<ReturnType<typeof useDoc.getState>['updateAnnotation']>[1]) {
      useDoc.getState().updateAnnotation(id, patch, { history: false })
    },
    /**
     * `fresh` sessions edit an annotation that was just created: its creation already pushed an
     * undo step, so typing is folded into it and an abandoned empty box leaves no trace.
     */
    finish(opts: { remove?: boolean } = {}) {
      if (done.current) return
      done.current = true
      const doc = useDoc.getState()
      if (opts.remove) {
        if (fresh) {
          doc.discardLast()
          return
        }
        doc.checkpoint(before.current)
        doc.live((s) => {
          const annotations = { ...s.annotations }
          for (const k of Object.keys(annotations)) annotations[k] = annotations[k].filter((a) => a.id !== id)
          return { ...s, annotations }
        })
        return
      }
      if (fresh) return
      if (doc.snapshot().annotations !== before.current.annotations) doc.checkpoint(before.current)
    },
  }
}

export function FreeTextEditor({ a, scale }: { a: FreeTextAnn; scale: number }) {
  const ref = useRef<HTMLTextAreaElement>(null)
  const session = useEditSession(a.id, !a.text)
  const textRef = useRef(a.text)
  textRef.current = a.text

  useEffect(() => {
    const t = ref.current
    if (!t) return
    t.focus({ preventScroll: true })
    t.setSelectionRange(t.value.length, t.value.length)
  }, [])
  useUnmount(() => session.finish({ remove: !textRef.current.trim() }))

  useLayoutEffect(() => {
    const t = ref.current
    if (!t) return
    t.style.height = '0px'
    const needed = t.scrollHeight
    t.style.height = `${Math.max(a.h, needed)}px`
    if (needed > a.h + 0.5) session.update({ h: needed })
  })

  return (
    <textarea
      ref={ref}
      value={a.text}
      placeholder="Type here"
      spellCheck
      onPointerDown={stop}
      onChange={(e) => session.update({ text: e.target.value })}
      onBlur={() => useUi.getState().setEditing(null)}
      onKeyDown={(e) => {
        e.stopPropagation()
        if (e.key === 'Escape') (e.target as HTMLTextAreaElement).blur()
      }}
      className="absolute m-0 resize-none overflow-hidden border-0 outline-none placeholder:text-black/35"
      style={{
        left: a.x,
        top: a.y,
        width: a.w,
        minHeight: a.h,
        padding: TEXT_PAD,
        color: a.color,
        background: a.background ?? 'rgb(99 102 241 / 0.06)',
        boxShadow: `0 0 0 ${1.5 / scale}px var(--accent)`,
        borderRadius: 1,
        pointerEvents: 'auto',
        overflowWrap: 'anywhere',
        ...cssFont(a),
      }}
    />
  )
}

interface TextEditEditorProps {
  a: TextEditAnn
  /** draft mode: changes are kept by the caller until onDone */
  onChange?: (text: string) => void
  onStyle?: (patch: Partial<TextEditAnn>) => void
  onDone: () => void
  onCancel?: () => void
}

export function TextEditEditor({ a, onChange, onStyle, onDone, onCancel }: TextEditEditorProps) {
  const ref = useRef<HTMLTextAreaElement>(null)
  const draft = Boolean(onChange)
  const session = useEditSession(a.id)
  const latest = useRef(a)
  latest.current = a

  useEffect(() => {
    const t = ref.current
    if (!t) return
    t.focus({ preventScroll: true })
    t.select()
  }, [])
  useUnmount(() => {
    if (!draft) session.finish({ remove: latest.current.text === latest.current.original })
  })

  useLayoutEffect(() => {
    const t = ref.current
    if (!t) return
    t.style.width = '0px'
    t.style.width = `${Math.max(a.w + 2, t.scrollWidth + 2)}px`
    t.style.height = '0px'
    t.style.height = `${t.scrollHeight}px`
  })

  const setText = (text: string) => (draft ? onChange!(text) : session.update({ text }))
  const setStyle = (patch: Partial<TextEditAnn>) => (draft ? onStyle?.(patch) : session.update(patch))
  const lineTop = a.baseline - a.fontSize * BASELINE_EM[a.fontFamily]

  return (
    <>
      <div
        className="absolute"
        style={{ left: a.x - 1, top: a.y - 1, width: a.w + 2, height: a.h + 2, background: a.cover }}
      />
      <textarea
        ref={ref}
        value={a.text}
        wrap="off"
        rows={1}
        spellCheck
        onPointerDown={stop}
        onChange={(e) => setText(e.target.value)}
        onBlur={(e) => {
          // keep editing when focus moves into the mini toolbar
          if ((e.relatedTarget as HTMLElement | null)?.closest('[data-text-toolbar]')) return
          onDone()
        }}
        onKeyDown={(e) => {
          e.stopPropagation()
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault()
            onDone()
          }
          if (e.key === 'Escape') {
            e.preventDefault()
            if (onCancel) onCancel()
            else {
              setText(a.original)
              onDone()
            }
          }
        }}
        className="absolute m-0 resize-none overflow-hidden whitespace-pre border-0 bg-transparent p-0 outline-none"
        style={{
          left: a.x,
          top: a.y + lineTop,
          color: a.color,
          boxShadow: '0 0 0 1px var(--accent)',
          pointerEvents: 'auto',
          ...cssFont(a),
        }}
      />
      <TextMiniToolbar a={a} onStyle={setStyle} anchor={{ x: a.x, y: a.y }} />
    </>
  )
}

function TextMiniToolbar({
  a,
  onStyle,
  anchor,
}: {
  a: TextStyle & { color: string }
  onStyle: (p: Partial<TextEditAnn>) => void
  anchor: { x: number; y: number }
}) {
  const scale = useUi((s) => s.zoom) * (96 / 72)
  const btn =
    'flex size-7 items-center justify-center rounded-lg text-fg-2 hover:bg-surface-3 hover:text-fg data-[on=true]:bg-accent-soft data-[on=true]:text-accent'
  const style: CSSProperties = {
    left: anchor.x,
    top: anchor.y,
    transform: `scale(${1 / scale}) translateY(calc(-100% - 8px))`,
    transformOrigin: '0 0',
    pointerEvents: 'auto',
  }
  const keep = (e: React.PointerEvent | React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
  }
  return (
    <div
      data-text-toolbar
      style={style}
      onPointerDown={keep}
      onMouseDown={keep}
      className="absolute z-10 flex animate-pop items-center gap-0.5 whitespace-nowrap rounded-xl border border-line bg-surface p-1 font-sans text-fg shadow-md"
    >
      <select
        aria-label="Font"
        value={a.fontFamily}
        onChange={(e) => onStyle({ fontFamily: e.target.value as FontFamily })}
        className="h-7 rounded-lg bg-transparent px-1 text-xs outline-none hover:bg-surface-3"
      >
        <option value="sans">Sans</option>
        <option value="serif">Serif</option>
        <option value="mono">Mono</option>
      </select>
      <button
        type="button"
        className={btn}
        aria-label="Smaller"
        onClick={() => onStyle({ fontSize: Math.max(4, a.fontSize - 1) })}
      >
        <Minus className="size-3.5" />
      </button>
      <span className="w-8 text-center text-xs tabular-nums">{Math.round(a.fontSize * 10) / 10}</span>
      <button
        type="button"
        className={btn}
        aria-label="Larger"
        onClick={() => onStyle({ fontSize: a.fontSize + 1 })}
      >
        <Plus className="size-3.5" />
      </button>
      <button
        type="button"
        className={btn}
        data-on={a.bold}
        aria-label="Bold"
        onClick={() => onStyle({ bold: !a.bold })}
      >
        <Bold className="size-3.5" />
      </button>
      <button
        type="button"
        className={btn}
        data-on={a.italic}
        aria-label="Italic"
        onClick={() => onStyle({ italic: !a.italic })}
      >
        <Italic className="size-3.5" />
      </button>
      <label
        className="relative ml-0.5 flex size-7 cursor-pointer items-center justify-center rounded-lg hover:bg-surface-3"
        aria-label="Text color"
      >
        <span className="size-4 rounded-full border border-line-strong" style={{ background: a.color }} />
        <input
          type="color"
          value={a.color.length === 7 ? a.color : '#000000'}
          onChange={(e) => onStyle({ color: e.target.value })}
          className="absolute inset-0 cursor-pointer opacity-0"
        />
      </label>
    </div>
  )
}

export function NotePopup({
  a,
  scale,
  page,
  autoFocus,
}: {
  a: NoteAnn
  scale: number
  page: PageRef
  autoFocus: boolean
}) {
  const ref = useRef<HTMLTextAreaElement>(null)
  const session = useEditSession(a.id, autoFocus && !a.text)
  const textRef = useRef(a.text)
  textRef.current = a.text
  const W = 248
  const placeLeft = (a.x + NOTE_SIZE + 6) * scale + W > page.width * scale && a.x * scale > W
  useEffect(() => {
    if (autoFocus) ref.current?.focus({ preventScroll: true })
  }, [autoFocus])
  useUnmount(() => session.finish())
  return (
    <div
      onPointerDown={stop}
      className="absolute z-20 animate-pop font-sans"
      style={{
        left: placeLeft ? a.x - 6 / scale : a.x + NOTE_SIZE + 6 / scale,
        top: a.y,
        transform: `scale(${1 / scale})${placeLeft ? ' translateX(-100%)' : ''}`,
        transformOrigin: '0 0',
        width: W,
        pointerEvents: 'auto',
      }}
    >
      <div className="overflow-hidden rounded-2xl border border-line bg-surface text-fg shadow-lg">
        <div
          className="flex items-center gap-2 border-line border-b px-3 py-2"
          style={{ background: `${a.color}33` }}
        >
          <span className="size-2.5 rounded-full" style={{ background: a.color }} />
          <span className="flex-1 truncate font-medium text-xs">{a.author || 'Note'}</span>
          <span className="text-[11px] text-fg-3">
            {new Date(a.updatedAt ?? a.createdAt).toLocaleDateString()}
          </span>
        </div>
        <textarea
          ref={ref}
          value={a.text}
          rows={4}
          placeholder="Add a comment…"
          onChange={(e) => session.update({ text: e.target.value })}
          onKeyDown={(e) => {
            e.stopPropagation()
            if (e.key === 'Escape') {
              useUi.getState().select([])
            }
          }}
          className="block w-full resize-none bg-transparent px-3 py-2 text-[13px] leading-relaxed outline-none placeholder:text-fg-3"
        />
        <div className="flex items-center gap-1 border-line border-t px-2 py-1.5">
          {SWATCHES.slice(2, 8).map((c) => (
            <button
              key={c}
              type="button"
              aria-label={`Color ${c}`}
              onClick={() => useDoc.getState().updateAnnotation(a.id, { color: c })}
              className="size-5 rounded-full border border-black/10"
              style={{
                background: c,
                outline: a.color === c ? '2px solid var(--accent)' : undefined,
                outlineOffset: 1,
              }}
            />
          ))}
          <div className="flex-1" />
          <button
            type="button"
            aria-label="Delete note"
            onClick={() => {
              useDoc.getState().deleteAnnotations([a.id])
              useUi.getState().select([])
            }}
            className="rounded-lg p-1.5 text-fg-3 hover:bg-danger/10 hover:text-danger"
          >
            <Trash2 className="size-4" />
          </button>
        </div>
      </div>
    </div>
  )
}
